import { useMemo, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useDispatch } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { usePermission } from "../../hooks/usePermission";
import { toggleSidebar } from "../../store/themeConfigSlice";
import ImageCustom from "../custom/image-custom";
import IconCaretsDown from "../icons/icon-carets-down";
import logo from "../icons/logo_laundry.png";
import { SIDEBAR_GROUPS, SidebarNavGroup } from "./sidebar-config";
// Tạm ẩn bộ lọc chi nhánh trên sidebar (yêu cầu nghiệp vụ). Xem thêm ghi chú trong BranchContext.
// import BranchSelector from "./BranchSelector";

const Sidebar = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const visibleGroups: SidebarNavGroup[] = useMemo(() => {
    return SIDEBAR_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) => hasPermission(item.permissionCodes ?? [])),
    })).filter((group) => group.items.length > 0);
  }, [hasPermission]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (id: string | number) => {
    const key = String(id);
    setOpenGroups((prev) => {
      const isCurrentlyOpen = !!prev[key];
      return isCurrentlyOpen ? {} : { [key]: true };
    });
  };
  
  const location = useLocation();

  return (
    <div>
      <nav
        className={`sidebar fixed min-h-screen h-full top-0 bottom-0 w-[260px] shadow-[5px_0_25px_0_rgba(94,92,154,0.07)] z-50 transition-all duration-300 bg-white`}
      >
        <div className="h-full bg-white flex flex-col min-h-0">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <NavLink to="/" className="flex items-center main-logo shrink-0 gap-2 transition-transform hover:scale-105 duration-500 ease-out">
              <ImageCustom className="inline w-[140px] ltr:-ml-1 rtl:-mr-1" url={logo} />
            </NavLink>

            <button
              type="button"
              className="flex items-center w-8 h-8 transition duration-300 rounded-full collapse-icon hover:bg-slate-100 text-slate-500 rtl:rotate-180"
              onClick={() => dispatch(toggleSidebar())}
            >
              <IconCaretsDown className="m-auto rotate-90" />
            </button>
          </div>

          {/* Branch Selector — tạm ẩn theo yêu cầu nghiệp vụ */}
          {/* <div className="px-4 py-3 border-b border-slate-200">
            <BranchSelector />
          </div> */}

          {/* Sidebar Menu */}
          <PerfectScrollbar className="flex-1 min-h-0 overflow-auto pb-6">
            <ul className="relative font-medium space-y-5 p-0">
              {visibleGroups.map((group) => {
                const key = String(group.id);
                const autoOpen = group.items.some((it) => location.pathname === it.path);
                const isOpen = openGroups[key] ?? autoOpen;

                return (
                  <li key={group.id} className="mb-3">
                    <button
                      type="button"
                      aria-expanded={!!isOpen}
                      onClick={() => toggleGroup(group.id as any)}
                      className={`w-full flex items-center justify-between px-5 py-3 text-[15px] font-semibold rounded-2xl border transition-all duration-300 ease-[cubic-bezier(0.25,0.8,0.25,1)] shadow-sm hover:shadow-md ${
                        isOpen
                          ? "bg-green-50 text-green-700 border-green-200 shadow-green-100/50"
                          : "bg-slate-50 text-slate-600 border-slate-100 hover:bg-slate-100"
                      }`}
                    >
                      <span className="uppercase tracking-wide truncate">{group.title}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        strokeWidth={2.2}
                        stroke="currentColor"
                        className={`w-4 h-4 transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                          isOpen ? "rotate-90 text-green-500" : "text-slate-400"
                        }`}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>

                    {isOpen && (
                      // ANIMATION UPDATE:
                      // origin-top: Animation bắt đầu từ cạnh trên
                      // animate-menuReveal: Gọi animation custom định nghĩa bên dưới
                      <ul className="mt-2 space-y-2 pl-4 pr-3 origin-top animate-menuReveal overflow-hidden">
                        {group.items.map((item, index) => {
                          const showBadge = item.badge !== undefined && item.badge != 0;
                          const badgeColor =
                            item.id === "stores"
                              ? "bg-green-600"
                              : item.id === "order-queue"
                              ? "bg-yellow-600 animate-pulse"
                              : "bg-red-600";
                          
                          const baseItemClass =
                            "flex items-center justify-between w-full px-5 py-2.5 text-[14px] text-slate-700 rounded-xl bg-white border border-transparent transition-all duration-300 ease-out hover:bg-slate-50 hover:pl-6 hover:shadow-sm";
                          const activeItemClass =
                            "bg-green-50 text-green-700 font-semibold border-green-200 ring-1 ring-green-200 hover:bg-green-50 hover:pl-5";

                          return (
                            <li 
                              key={item.id}
                              // Stagger delay giả lập bằng CSS inline để các item xuất hiện lần lượt (nhanh dần)
                              style={{ animationDelay: `${index * 50}ms` }}
                              className="animate-fadeInItem opacity-0 fill-mode-forwards"
                            >
                              <NavLink
                                to={item.path}
                                end
                                className={({ isActive }) =>
                                  `${baseItemClass} ${isActive ? activeItemClass : ""}`
                                }
                              >
                                <div className="flex items-center gap-3">
                                  <span className={`w-2 h-2 rounded-full transition-all duration-300 ${location.pathname === item.path ? "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)] scale-110" : "bg-slate-300 group-hover:bg-slate-400"}`} />
                                  <span className="truncate">{item.title}</span>
                                </div>
                                {showBadge && (
                                  <span
                                    className={`ml-auto text-white rounded-full px-2 py-[2px] text-[12px] font-semibold min-w-[20px] text-center shadow-sm ${badgeColor}`}
                                  >
                                    {item.badge}
                                  </span>
                                )}
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </PerfectScrollbar>
        </div>
      </nav>

      {/* --- PHẦN STYLE NÂNG CẤP --- */}
      <style>{`
        /* 1. Animation mở menu cha (Cái khung UL) */
        @keyframes menuReveal {
          0% {
            opacity: 0;
            transform: translateY(-8px) scale(0.97); /* Hơi nhỏ lại 1 chút và dịch lên */
            filter: blur(3px); /* Hiệu ứng mờ ảo */
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        .animate-menuReveal {
          animation: menuReveal 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        /* 2. Animation từng item con (Stagger effect) */
        @keyframes fadeInItem {
          from {
            opacity: 0;
            transform: translateX(-10px); /* Trượt từ trái sang nhẹ */
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeInItem {
          animation: fadeInItem 0.3s ease-out forwards;
        }
        
        /* Class tiện ích để giữ trạng thái cuối cùng của animation */
        .fill-mode-forwards {
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
};

export default Sidebar;